import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { GraduationCap, Clock, ArrowRight } from "lucide-react";
import { listAcademyCourses } from "@/lib/api/academy.functions";
import { getMyEnrollments } from "@/lib/api/enrollments.functions";

export const Route = createFileRoute("/_authenticated/portal/academy/")({
  component: AcademyList,
  head: () => ({ meta: [{ title: "NDLCS Academy — Free Skills Courses · NDLCS" }] }),
});

function AcademyList() {
  const fetchCourses = useServerFn(listAcademyCourses);
  const fetchEnrolments = useServerFn(getMyEnrollments);
  const { data } = useQuery({ queryKey: ["academy-courses"], queryFn: () => fetchCourses() });
  const { data: enrol } = useQuery({
    queryKey: ["my-enrollments"],
    queryFn: () => fetchEnrolments(),
  });
  const enrolledCodes = new Set(
    (enrol?.enrollments ?? [])
      .filter((e: any) => e.course_type === "free")
      .map((e: any) => e.module_code),
  );

  const grouped = (data?.courses ?? []).reduce<Record<string, any[]>>((acc, c: any) => {
    (acc[c.category] ||= []).push(c);
    return acc;
  }, {});

  return (
    <section>
      <header className="mb-6">
        <p className="text-xs uppercase tracking-[0.3em] text-gold">NDLCS Academy</p>
        <h2 className="mt-1 font-display text-2xl text-navy">
          Free skills courses — modules, quizzes, final exam
        </h2>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Work through short modules, pass each module quiz, then sit the final
          exam. Once you pass, an NDLCS administrator reviews and issues your
          tamper-evident certificate.
        </p>
        <div className="mt-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-xs text-amber-900">
          <strong>Disclaimer:</strong> NDLCS Academy courses are not accredited
          and do not carry NQF / SAQA recognition. Completion earns an NDLCS
          professional-development certificate only. By enrolling you accept
          the enrolment contract emailed to you.
        </div>
      </header>

      {Object.entries(grouped).map(([category, items]) => (
        <div key={category} className="mb-8">
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-widest text-navy">
            {category}
          </h3>
          <ul className="grid gap-3 md:grid-cols-2">
            {items.map((c: any) => {
              const enrolled = enrolledCodes.has(c.code);
              return (
                <li key={c.slug} className="flex flex-col rounded-lg border border-input bg-card p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-gold">
                        {c.code} · ~{c.estimated_hours}h · pass {c.pass_threshold}%
                      </p>
                      <h4 className="mt-1 font-display text-lg text-navy">{c.title}</h4>
                      <p className="mt-1 text-sm text-muted-foreground">{c.summary}</p>
                    </div>
                    <GraduationCap className="h-5 w-5 shrink-0 text-gold" />
                  </div>
                  <div className="mt-4 flex items-center justify-between gap-2">
                    <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                      <Clock className="h-3 w-3" /> Self-paced
                    </span>
                    <Link
                      to="/portal/academy/$slug"
                      params={{ slug: c.slug }}
                      className="inline-flex items-center gap-2 rounded-md bg-gold px-4 py-2 text-sm font-semibold text-navy shadow-sm hover:opacity-90"
                    >
                      {enrolled ? "Continue course" : "Start course"} <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </section>
  );
}